import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useAcceptInvitation, useInvitationPreview } from "../api/users.js";
import { useSignOut } from "../api/queries.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { Alert, Button, Spinner, TextField } from "../ui/primitives.js";
import { AuthLayout } from "./auth.js";

/** The path of an invitation link; the token travels in the fragment (`/invite#<token>`). */
export const INVITE_PATH = "/invite";

/** The token of the invitation link this page was opened with. */
export const inviteToken = (): string => window.location.hash.replace(/^#/, "");

/**
 * Accepting an invitation (docs/users.md): the person sees the organisation
 * and their e-mail, chooses a name and a password and is signed in; like
 * everyone, they then add a passkey or an authenticator app first.
 */
export function AcceptInvitePage({
  signedIn,
  onDone,
}: {
  signedIn: boolean;
  /** Leaves the invitation screen (after accepting, or to sign in instead). */
  onDone: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [token] = useState(inviteToken);
  const preview = useInvitationPreview(token);
  const accept = useAcceptInvitation();
  const signOut = useSignOut();

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    accept.mutate(
      {
        token,
        name: String(form.get("name") ?? "").trim(),
        password: String(form.get("password") ?? ""),
        locale: i18n.resolvedLanguage === "ru" ? "ru" : "en",
      },
      // The link is used up: the app starts at its root, then asks for a second factor.
      { onSuccess: onDone },
    );
  };

  if (token === "" || preview.isError)
    return (
      <AuthLayout title={t("invite.invalidTitle")}>
        <Alert tone="danger">{t("invite.invalidLead")}</Alert>
        <Button variant="secondary" onClick={onDone}>
          {t("invite.toSignIn")}
        </Button>
      </AuthLayout>
    );
  if (preview.isPending)
    return (
      <AuthLayout title={t("invite.title")}>
        <Spinner label={t("common.loading")} />
      </AuthLayout>
    );

  const { organizationName, email, role } = preview.data;
  return (
    <AuthLayout
      title={t("invite.title")}
      lead={t("invite.lead", { organization: organizationName, role: t(`users.role.${role}`) })}
    >
      {signedIn && (
        <Alert tone="warn">
          <p>{t("invite.signedIn")}</p>
          <Button
            variant="secondary"
            className="mt-2"
            busy={signOut.isPending}
            onClick={() => signOut.mutate(undefined)}
          >
            {t("common.signOut")}
          </Button>
        </Alert>
      )}
      <form onSubmit={submit} className="flex flex-col gap-4">
        <TextField label={t("invite.email")} value={email} readOnly autoComplete="username" />
        <TextField
          label={t("invite.name")}
          name="name"
          required
          maxLength={100}
          autoComplete="name"
          error={fieldError(accept.error, "name", t)}
        />
        <TextField
          label={t("invite.password")}
          name="password"
          type="password"
          required
          minLength={12}
          autoComplete="new-password"
          hint={t("invite.passwordHint")}
          error={fieldError(accept.error, "password", t)}
        />
        <p className="text-xs text-muted">{t("invite.nextStep")}</p>
        <ErrorMessage error={accept.error} />
        <Button type="submit" busy={accept.isPending} disabled={signedIn}>
          {t("invite.submit")}
        </Button>
      </form>
    </AuthLayout>
  );
}
